<template>
  <div>
    <!-- Snackbar -->
    <SnackbarComponent
      v-model="snackbar.show"
      :message="snackbar.message"
      :color="snackbar.color"
    />

    <!-- Dialog -->
    <v-dialog v-model="dialog" max-width="500px" persistent>
      <v-card>
        <v-card-title class="text-h5 pa-4"> Renewal Plan </v-card-title>

        <v-divider />

        <!-- Form -->
        <v-card-text class="pa-6">
          <v-form ref="form">
            <v-radio-group v-model="formData.planType" inline class="mb-4">
              <v-radio
                v-for="option in planTypeOptions"
                :key="option.value"
                :label="option.label"
                :value="option.value"
              />
            </v-radio-group>

            <!-- ANNUAL & HYBRID -->
            <v-text-field
              v-if="formData.planType !== 'MRR'"
              v-model="formData.annualPrice"
              label="Annual Price Per User"
              required
              :rules="[rules.required]"
              type="text"
              @keypress="allowNumbersOnly"
              @paste.prevent
              variant="outlined"
              density="compact"
            />

            <v-text-field
              v-if="formData.planType !== 'MRR'"
              v-model="formData.noOfUsers"
              label="Number of Users"
              required
              :rules="[rules.required]"
              type="text"
              @keypress="allowNumbersOnly"
              @paste.prevent
              variant="outlined"
              density="compact"
            />

            <!-- MRR & HYBRID -->
            <v-text-field
              v-if="formData.planType !== 'ANNUAL'"
              v-model="formData.licensecount"
              label="License Count"
              required
              :rules="[rules.required]"
              type="text"
              @keypress="allowNumbersOnly"
              @paste.prevent
              variant="outlined"
              density="compact"
            />
          </v-form>
        </v-card-text>

        <v-divider />

        <!-- Actions -->
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn variant="text" @click="handleClose"> Cancel </v-btn>

          <v-btn
            color="primary"
            variant="flat"
            :loading="loading"
            :disabled="loading"
            @click="handleSubmit"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { generateClient } from "aws-amplify/api";
import { initiate_license_renewal } from "@/graphql/mutations";
import SnackbarComponent from "@/components/Extras/Snackbar.vue";

const client = generateClient();

export default {
  name: "RenewalPlanDialog",
  components: {
    SnackbarComponent,
  },

  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    organization: {
      type: String,
      required: true,
    },
  },

  emits: ["update:modelValue", "saved"],

  data() {
    return {
      loading: false,
      formData: {
        planType: "HYBRID", // default
        annualPrice: "",
        noOfUsers: "",
        licensecount: "",
      },
      rules: {
        required: (v) => !!v || "This field is required",
      },
      snackbar: {
        show: false,
        message: "",
        color: "success",
      },
    };
  },

  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },

    planTypeOptions() {
      const type = this.organization.license_type;

      if (type === "HYBRID") {
        return [{ label: "Both (Hybrid)", value: "HYBRID" }];
      }

      if (type === "ANNUAL") {
        return [
          { label: "Annual", value: "ANNUAL" },
          { label: "Both (Hybrid)", value: "HYBRID" },
        ];
      }

      if (type === "MRR") {
        return [
          { label: "MRR", value: "MRR" },
          { label: "Both (Hybrid)", value: "HYBRID" },
        ];
      }

      return [];
    },
  },

  watch: {
    modelValue(val) {
      if (val) {
        this.formData.planType = this.organization.license_type;
      } else {
        this.resetForm();
      }
    },
  },

  methods: {
    resetForm() {
      this.formData = {
        planType: this.organization.license_type,
        annualPrice: "",
        noOfUsers: "",
        licensecount: "",
      };

      this.$nextTick(() => {
        this.$refs.form?.resetValidation();
      });
    },

    handleClose() {
      this.resetForm();
      this.dialog = false;
    },

    allowNumbersOnly(e) {
      if (!/[0-9]/.test(e.key)) {
        e.preventDefault();
      }
    },

    async handleSubmit() {
      const { valid } = await this.$refs.form.validate();
      if (!valid) return;

      this.loading = true;

      try {
        const input = {
          organization_id: this.organization.organization_id,
          renewal_plan_type: this.formData.planType,
        };

        if (this.formData.planType !== "MRR") {
          input.renewal_price = Number(this.formData.annualPrice);
          input.renewal_user_count = Number(this.formData.noOfUsers);
        }

        if (this.formData.planType !== "ANNUAL") {
          input.renewal_mrr_count = Number(this.formData.licensecount);
        }
        const response = await client.graphql({
          query: initiate_license_renewal,
          variables: { input },
        });

        const responseData = JSON.parse(response.data.initiate_license_renewal);

        if (responseData.status === "SUCCESS") {
          this.snackbar = {
            show: true,
            message:
              responseData.status_message ||
              "Renewal plan updated successfully",
            color: "success",
          };

          this.$emit("saved", input);
          this.resetForm();
          this.handleClose();
        } else {
          this.snackbar = {
            show: true,
            message:
              responseData.status_message || "Failed to update renewal plan",
            color: "error",
          };
        }
      } catch (error) {
        console.error(error);
        this.snackbar = {
          show: true,
          message: error.errors?.[0]?.message || "Something went wrong",
          color: "error",
        };
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
</style>
